import React, {
  useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  assignmentsApi, fieldApi, salaryAckApi, complaintsApi, servicesApi,
  settingsApi, customersApi, installationsApi, salesApi,
} from '../api/client';
import { Spin, useFlash, parseErr } from '../components/UI';
import {
  ClipCheck, Calendar, Wallet, Phone, MapPin, Navigation,
  AlertTri, Check, Tool,
} from '../components/Icons';
import { useAuth } from '../context/AuthContext';

// ── Helpers ───────────────────────────────────────────────────────────────────

const MNF = ['January','February','March','April','May','June',
             'July','August','September','October','November','December'];
const DS  = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const fmtMoney = n => '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 });

const nowIST = () => {
  const now = new Date();
  return new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
};

const todayStr = () => {
  const d = nowIST();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const playBeep = () => {
  try {
    const ctx  = new (window.AudioContext || window.webkitAudioContext)();
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = 880; osc.type = 'sine';
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.5);
  } catch { }
};

const STATUS_COLOR = {
  open: '#dc2626', in_progress: '#d97706', resolved: '#16a34a',
  pending: '#dc2626', completed: '#16a34a', closed: '#6b7280',
  paid: '#16a34a', partial: '#d97706',
};

const PRIORITY_COLOR = { high: '#b45309', critical: '#dc2626' };

const SEEN_KEY = 'sakthi_field_seen';
const getSeenIds = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')); }
  catch { return new Set(); }
};
const saveSeenIds = ids => localStorage.setItem(SEEN_KEY, JSON.stringify([...ids]));

// ── Signature Pad ─────────────────────────────────────────────────────────────

const SignaturePad = forwardRef((_, ref) => {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const hasDrawn  = useRef(false);

  useImperativeHandle(ref, () => ({
    getDataURL: () => canvasRef.current?.toDataURL('image/png'),
    isEmpty:    () => !hasDrawn.current,
    clear: () => {
      const c = canvasRef.current;
      if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
      hasDrawn.current = false;
    },
  }));

  const pos = (e, c) => {
    const r = c.getBoundingClientRect();
    const sx = c.width / r.width, sy = c.height / r.height;
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - r.left) * sx, y: (src.clientY - r.top) * sy };
  };

  const start = e => {
    e.preventDefault();
    const c = canvasRef.current, ctx = c.getContext('2d');
    const p = pos(e, c);
    drawing.current = true;
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
  };

  const move = e => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current, ctx = c.getContext('2d');
    const p = pos(e, c);
    ctx.lineWidth = 2.5; ctx.strokeStyle = '#1e3a5f';
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.lineTo(p.x, p.y); ctx.stroke();
    hasDrawn.current = true;
  };

  const end = () => { drawing.current = false; };

  return (
    <canvas
      ref={canvasRef} width={600} height={180}
      style={{ border:'1.5px solid #ddd', borderRadius:8, cursor:'crosshair', width:'100%', background:'#fafbfc', display:'block', touchAction:'none' }}
      onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
      onTouchStart={start} onTouchMove={move} onTouchEnd={end}
    />
  );
});

// ── Shared: Customer Card with location editing ───────────────────────────────

function CustomerCard({ task, show: showFlash }) {
  const [currentAddress, setCurrentAddress] = useState(task.customer_address || '');
  const [editingAddr,    setEditingAddr]    = useState(false);
  const [newAddr,        setNewAddr]        = useState('');
  const [addrSaving,     setAddrSaving]     = useState(false);
  const [gpsStatus,      setGpsStatus]      = useState(null);
  const [savedLat,       setSavedLat]       = useState(task.location_lat ?? null);
  const [savedLng,       setSavedLng]       = useState(task.location_lng ?? null);
  const [installations,  setInstallations]  = useState([]);
  const [instOpen,       setInstOpen]       = useState(false);

  const displayName  = task.customer_name !== '—' ? task.customer_name : (task.walk_in_name || '—');
  const displayPhone = task.customer_phone || task.walk_in_phone;

  const mapsUrl = savedLat && savedLng
    ? `https://www.google.com/maps?q=${savedLat},${savedLng}`
    : currentAddress
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(currentAddress)}`
      : null;

  useEffect(() => {
    if (task.customer_id) {
      installationsApi.list(task.customer_id)
        .then(({ data }) => setInstallations(data || []))
        .catch(() => {});
    }
  }, [task.customer_id]);

  const startEditAddr = () => {
    setNewAddr(currentAddress);
    setEditingAddr(true);
  };

  const saveAddress = async () => {
    if (!task.customer_id || !newAddr.trim()) return;
    setAddrSaving(true);
    try {
      await customersApi.update(task.customer_id, { address: newAddr.trim() });
      setCurrentAddress(newAddr.trim());
      setEditingAddr(false);
      showFlash('Address updated!');
    } catch {
      showFlash('Failed to update address.', 'error');
    } finally { setAddrSaving(false); }
  };

  const saveLocation = () => {
    if (!task.customer_id) return;
    if (!navigator.geolocation) { showFlash('GPS not supported on this device.', 'error'); return; }
    setGpsStatus('getting');
    navigator.geolocation.getCurrentPosition(
      async pos => {
        setGpsStatus('saving');
        try {
          await customersApi.saveLocation(task.customer_id, pos.coords.latitude, pos.coords.longitude);
          setSavedLat(pos.coords.latitude);
          setSavedLng(pos.coords.longitude);
          setGpsStatus('saved');
          showFlash('GPS location saved to customer profile!');
        } catch {
          setGpsStatus('error');
          showFlash('Failed to save GPS location.', 'error');
        }
      },
      () => { setGpsStatus('error'); showFlash('Could not get GPS location.', 'error'); },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const card = { background:'#fff', borderRadius:12, padding:16, marginBottom:12, boxShadow:'0 1px 4px rgba(0,0,0,0.08)' };
  const secLabel = { fontSize:11, fontWeight:700, color:'#888', marginBottom:10, letterSpacing:0.5 };

  return (
    <>
      {/* Customer basic info */}
      <div style={card}>
        <div style={secLabel}>CUSTOMER</div>
        <div style={{ fontSize:17, fontWeight:700, color:'#1a2744', marginBottom:12 }}>{displayName}</div>
        {displayPhone && (
          <a href={`tel:${displayPhone}`} style={{ display:'flex', alignItems:'center', gap:8, color:'var(--navy)', fontSize:14, fontWeight:600, marginBottom:10, textDecoration:'none' }}>
            <Phone s={16}/> {displayPhone}
          </a>
        )}

        {/* Address row */}
        {currentAddress && !editingAddr && (
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8, marginBottom:10 }}>
            {mapsUrl ? (
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                style={{ display:'flex', alignItems:'flex-start', gap:8, color:'#2563eb', fontSize:13, textDecoration:'none', flex:1 }}>
                <MapPin s={16} style={{ flexShrink:0, marginTop:1 }}/> <span style={{ lineHeight:1.5 }}>{currentAddress}</span>
              </a>
            ) : (
              <div style={{ display:'flex', alignItems:'flex-start', gap:8, fontSize:13, color:'#374151', flex:1 }}>
                <MapPin s={16} style={{ flexShrink:0, marginTop:1 }}/> <span style={{ lineHeight:1.5 }}>{currentAddress}</span>
              </div>
            )}
            {task.customer_id && (
              <button onClick={startEditAddr}
                style={{ fontSize:11, fontWeight:600, color:'#2563eb', background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:6, padding:'4px 10px', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
                Edit
              </button>
            )}
          </div>
        )}

        {/* Address edit form */}
        {editingAddr && (
          <div style={{ marginBottom:10, padding:12, background:'#f0f7ff', borderRadius:10, border:'1.5px solid #bfdbfe' }}>
            <div style={{ fontSize:12, fontWeight:600, color:'#1e3a5f', marginBottom:8 }}>Update Customer Address</div>
            <textarea
              value={newAddr}
              onChange={e => setNewAddr(e.target.value)}
              rows={3}
              placeholder="Type the correct address…"
              style={{ width:'100%', border:'1.5px solid #bfdbfe', borderRadius:8, padding:'8px 10px', fontSize:13, resize:'vertical', boxSizing:'border-box', fontFamily:'inherit', marginBottom:8 }}
            />
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={saveAddress} disabled={addrSaving || !newAddr.trim()}
                style={{ flex:1, padding:'9px', fontSize:13, fontWeight:700, background:'var(--navy)', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', opacity: addrSaving ? 0.7 : 1 }}>
                {addrSaving ? 'Saving…' : 'Save Address'}
              </button>
              <button onClick={() => setEditingAddr(false)}
                style={{ padding:'9px 14px', fontSize:13, background:'#fff', border:'1.5px solid #ddd', borderRadius:8, cursor:'pointer' }}>
                Cancel
              </button>
            </div>
            {newAddr.trim() && (
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(newAddr)}`}
                target="_blank" rel="noopener noreferrer"
                style={{ display:'block', marginTop:8, fontSize:12, color:'#2563eb', textAlign:'center' }}>
                Verify on Google Maps ↗
              </a>
            )}
          </div>
        )}

        {/* No address yet */}
        {!currentAddress && !editingAddr && task.customer_id && (
          <button onClick={startEditAddr}
            style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'#2563eb', background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8, padding:'8px 12px', cursor:'pointer', marginBottom:10 }}>
            <MapPin s={14}/> Add Address
          </button>
        )}

        {/* GPS section */}
        {task.customer_id && (
          <div style={{ borderTop:'1px solid #f0f0f0', paddingTop:12, marginTop:4 }}>
            {savedLat && savedLng ? (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, flexWrap:'wrap' }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#16a34a', fontWeight:600 }}>
                  <Navigation s={14}/> GPS location saved
                </div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <a href={`https://www.google.com/maps?q=${savedLat},${savedLng}`} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize:12, color:'#2563eb', fontWeight:600, textDecoration:'none' }}>
                    Open in Maps
                  </a>
                  <button onClick={saveLocation} disabled={gpsStatus === 'getting' || gpsStatus === 'saving'}
                    style={{ fontSize:11, color:'#555', background:'#f5f5f5', border:'1px solid #ddd', borderRadius:6, padding:'4px 10px', cursor:'pointer' }}>
                    {gpsStatus === 'getting' ? 'Getting…' : gpsStatus === 'saving' ? 'Saving…' : 'Update GPS'}
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={saveLocation} disabled={gpsStatus === 'getting' || gpsStatus === 'saving'}
                style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, fontWeight:600, color:'#fff', background:'var(--navy)', border:'none', borderRadius:8, padding:'9px 14px', cursor:'pointer', opacity: (gpsStatus==='getting'||gpsStatus==='saving') ? 0.6 : 1 }}>
                <Navigation s={14}/>
                {gpsStatus === 'getting' ? 'Getting GPS…' : gpsStatus === 'saving' ? 'Saving…' : 'Pin Location on Map (GPS)'}
              </button>
            )}
            {gpsStatus === 'error' && (
              <div style={{ fontSize:11, color:'#dc2626', marginTop:6 }}>Could not get GPS. Make sure location permission is granted.</div>
            )}
          </div>
        )}
      </div>

      {/* Installed products — collapsible */}
      {installations.length > 0 && (
        <div style={card}>
          <button
            onClick={() => setInstOpen(o => !o)}
            style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
              width:'100%', background:'none', border:'none', padding:0, cursor:'pointer' }}
          >
            <span style={secLabel}>INSTALLED PRODUCTS ({installations.length})</span>
            <span style={{ fontSize:16, color:'#6b7280', transform: instOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition:'transform 0.2s' }}>▾</span>
          </button>
          {instOpen && (
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginTop:10 }}>
              {installations.map(inst => {
                const warrantyEnd = inst.installation_date && inst.warranty_months
                  ? new Date(new Date(inst.installation_date).getTime() + inst.warranty_months * 30 * 24 * 3600 * 1000)
                  : null;
                const inWarranty = warrantyEnd ? warrantyEnd > new Date() : null;
                return (
                  <div key={inst.id} style={{ background:'#f8f9fb', borderRadius:8, padding:'10px 12px' }}>
                    <div style={{ fontWeight:700, fontSize:13, color:'#1a2744', marginBottom:3 }}>{inst.product_name}</div>
                    {inst.serial_number && (
                      <div style={{ fontSize:11, color:'#888', marginBottom:3 }}>S/N: {inst.serial_number}</div>
                    )}
                    <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                      {inst.installation_date && (
                        <span style={{ fontSize:11, color:'#555' }}>Installed: {inst.installation_date}</span>
                      )}
                      {inWarranty !== null && (
                        <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10,
                          background: inWarranty ? '#dcfce7' : '#fee2e2',
                          color: inWarranty ? '#16a34a' : '#dc2626' }}>
                          {inWarranty ? `Warranty till ${warrantyEnd.toLocaleDateString('en-IN', {dateStyle:'medium'})}` : 'Warranty Expired'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ── Complaint Detail ──────────────────────────────────────────────────────────

function ComplaintDetail({ task, user, onBack, onSaved }) {
  const [status,        setStatus]        = useState(task.status);
  const [addRemark,     setAddRemark]     = useState('');
  const [chargeItems, setChargeItems] = useState(() => {
    try { return task.charge_items ? JSON.parse(task.charge_items) : [{ type:'Service Charge', amount:'' }]; }
    catch { return [{ type:'Service Charge', amount:'' }]; }
  });
  const [amount,    setAmount]    = useState(
    task.amount_received != null ? String(task.amount_received) : ''
  );
  const [payMode,      setPayMode]      = useState(task.payment_mode || 'Cash');
  const [saving,       setSaving]       = useState(false);
  const [upiInfo,      setUpiInfo]      = useState(null);
  const [paySuccess,   setPaySuccess]   = useState(false);
  const [payPhoto,     setPayPhoto]     = useState(null);
  const [serialPhoto,  setSerialPhoto]  = useState(task.serial_number_photo || null);
  const photoInputRef       = useRef(null);
  const serialPhotoInputRef = useRef(null);
  const { show, Flash } = useFlash();

  useEffect(() => {
    settingsApi.get().then(({ data }) => setUpiInfo(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const total = chargeItems.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    if (total > 0) setAmount(String(total));
  }, [chargeItems]);

  const upiQrUrl = (() => {
    if (payMode !== 'UPI' || !amount || Number(amount) <= 0 || !upiInfo?.upi_id) return null;
    const upiStr = `upi://pay?pa=${upiInfo.upi_id}&pn=${encodeURIComponent(upiInfo.upi_name || 'Sakthi Solar')}&am=${Number(amount).toFixed(2)}&cu=INR&tn=${task.ref_code}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(upiStr)}`;
  })();

  const save = async () => {
    setSaving(true);
    try {
      const now = nowIST();
      const ts = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
      const newNotes = addRemark.trim()
        ? (task.resolution_notes ? task.resolution_notes + '\n' : '') + `[${ts}] ${user.name}: ${addRemark.trim()}`
        : undefined;
      await complaintsApi.update(task.id, {
        status,
        ...(newNotes !== undefined && { resolution_notes: newNotes }),
        charge_items: JSON.stringify(chargeItems.filter(i => i.amount !== '')),
        service_charge: chargeItems.reduce((s, i) => s + (Number(i.amount) || 0), 0) || undefined,
        ...(amount && { amount_received: Number(amount) }),
        payment_mode: payMode,
        ...(serialPhoto && { serial_number_photo: serialPhoto }),
      });
      show('Saved successfully!');
      onSaved();
    } catch (e) {
      show(parseErr(e, 'Failed to save.'), 'error');
    } finally { setSaving(false); }
  };

  const confirmPayment = (photoData) => {
    if (!amount || Number(amount) <= 0) { show('Enter an amount first.', 'error'); return; }
    setPaySuccess(true);
    complaintsApi.update(task.id, {
      status: 'resolved',
      amount_received: Number(amount),
      payment_mode: 'UPI',
      ...(photoData && { payment_screenshot: photoData }),
    }).catch(() => {});
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPayPhoto(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSerialPhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setSerialPhoto(ev.target.result);
    reader.readAsDataURL(file);
  };

  const generateServiceBill = () => {
    const items   = chargeItems.filter(i => Number(i.amount) > 0);
    const charge  = items.reduce((s, i) => s + Number(i.amount), 0);
    const paid    = amount ? Number(amount) : 0;
    const balance = charge - paid;
    const dateStr = new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
    const custName = task.customer_name !== '—' ? task.customer_name : (task.walk_in_name || '—');
    const custPhone = task.customer_phone || task.walk_in_phone || '';
    const custAddr = task.customer_address || '';

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Service Bill — ${task.ref_code}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: Arial, sans-serif; font-size:13px; color:#1a2744; padding:24px; }
  @media print { body { padding:0; } button { display:none; } }
  .header { text-align:center; border-bottom:2px solid #1a2744; padding-bottom:12px; margin-bottom:16px; }
  .header h1 { font-size:22px; font-weight:900; color:#1a2744; letter-spacing:0.5px; }
  .header p { font-size:11px; color:#555; margin-top:3px; }
  .bill-title { text-align:center; font-size:14px; font-weight:700; letter-spacing:2px; color:#888; margin-bottom:16px; text-transform:uppercase; }
  .row { display:flex; justify-content:space-between; margin-bottom:6px; }
  .label { color:#888; font-size:12px; }
  .value { font-weight:600; font-size:12px; text-align:right; }
  .section { background:#f8f9fb; border-radius:8px; padding:12px; margin-bottom:14px; }
  .section-title { font-size:10px; font-weight:700; color:#888; letter-spacing:1px; text-transform:uppercase; margin-bottom:8px; }
  table { width:100%; border-collapse:collapse; margin-bottom:14px; }
  th { background:#1a2744; color:#fff; padding:8px 10px; font-size:11px; text-align:left; }
  td { padding:8px 10px; font-size:12px; border-bottom:1px solid #eee; }
  .amount-row td { font-weight:700; }
  .total-row td { background:#1a2744; color:#fff; font-weight:800; font-size:14px; }
  .balance-positive { color:#dc2626; }
  .balance-zero { color:#16a34a; }
  .footer { text-align:center; font-size:11px; color:#888; border-top:1px solid #ddd; padding-top:12px; margin-top:16px; }
  .print-btn { display:block; margin:16px auto 0; padding:10px 28px; background:#1a2744; color:#fff; border:none; border-radius:8px; font-size:14px; font-weight:700; cursor:pointer; }
</style></head><body>
<div class="header">
  <h1>Sakthi Solar Solutions</h1>
  <p>Inverters • Batteries • UPS • Solar</p>
</div>
<div class="bill-title">Service Bill</div>

<div class="section">
  <div class="section-title">Bill Details</div>
  <div class="row"><span class="label">Bill No.</span><span class="value">${task.ref_code}</span></div>
  <div class="row"><span class="label">Date</span><span class="value">${dateStr}</span></div>
  <div class="row"><span class="label">Staff</span><span class="value">${task.assigned_to || '—'}</span></div>
</div>

<div class="section">
  <div class="section-title">Customer Details</div>
  <div class="row"><span class="label">Name</span><span class="value">${custName}</span></div>
  ${custPhone ? `<div class="row"><span class="label">Phone</span><span class="value">${custPhone}</span></div>` : ''}
  ${custAddr  ? `<div class="row"><span class="label">Address</span><span class="value" style="max-width:60%;text-align:right;">${custAddr}</span></div>` : ''}
</div>

<div class="section">
  <div class="section-title">Complaint / Service Details</div>
  <div class="row"><span class="label">Complaint Type</span><span class="value">${task.task_detail || '—'}</span></div>
  ${task.description ? `<div class="row"><span class="label">Description</span><span class="value" style="max-width:60%;text-align:right;">${task.description}</span></div>` : ''}
  <div class="row"><span class="label">Status</span><span class="value">${status.replace('_',' ').toUpperCase()}</span></div>
</div>

<table>
  <tr><th>Description</th><th style="text-align:right">Amount (₹)</th></tr>
  ${items.length > 0 ? items.map(i => `<tr><td>${i.type}</td><td style="text-align:right">${Number(i.amount).toLocaleString('en-IN', { minimumFractionDigits:2 })}</td></tr>`).join('') : '<tr><td>Service Charge</td><td style="text-align:right">—</td></tr>'}
  ${items.length > 1 ? `<tr style="background:#f0f4ff"><td style="font-weight:700">Total Charges</td><td style="text-align:right;font-weight:700">${charge.toLocaleString('en-IN', { minimumFractionDigits:2 })}</td></tr>` : ''}
  <tr class="amount-row"><td>Amount Paid</td><td style="text-align:right">${paid > 0 ? paid.toLocaleString('en-IN', { minimumFractionDigits:2 }) : '0.00'}</td></tr>
  ${paid > 0 ? `<tr><td style="font-size:11px;color:#888;">Payment Mode</td><td style="text-align:right;font-size:11px;color:#888;">${payMode}</td></tr>` : ''}
  <tr class="total-row">
    <td>Balance Due</td>
    <td style="text-align:right" class="${balance <= 0 ? 'balance-zero' : 'balance-positive'}">
      ₹ ${Math.max(0, balance).toLocaleString('en-IN', { minimumFractionDigits:2 })}
    </td>
  </tr>
</table>

<div class="footer">
  <p>Thank you for choosing Sakthi Solar Solutions!</p>
  <p style="margin-top:4px;">For support, please retain this bill.</p>
</div>
<button class="print-btn" onclick="window.print()">Print / Save PDF</button>
</body></html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  const card = { background:'#fff', borderRadius:12, padding:16, marginBottom:12, boxShadow:'0 1px 4px rgba(0,0,0,0.08)' };
  const secLabel = { fontSize:11, fontWeight:700, color:'#888', marginBottom:10, letterSpacing:0.5 };

  if (paySuccess) {
    return (
      <div style={{ position:'fixed', inset:0, zIndex:999, background:'#16a34a', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', fontFamily:'Inter,-apple-system,sans-serif', padding:32, textAlign:'center' }}>
        <div style={{ width:100, height:100, borderRadius:'50%', background:'rgba(255,255,255,0.2)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:28, animation:'popIn 0.4s cubic-bezier(0.175,0.885,0.32,1.275) both' }}>
          <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div style={{ fontSize:26, fontWeight:800, color:'#fff', marginBottom:8 }}>Payment Received!</div>
        <div style={{ fontSize:36, fontWeight:900, color:'#fff', marginBottom:8, letterSpacing:-0.5 }}>{fmtMoney(Number(amount))}</div>
        <div style={{ fontSize:14, color:'rgba(255,255,255,0.8)', marginBottom:6 }}>via UPI</div>
        <div style={{ background:'rgba(255,255,255,0.2)', borderRadius:10, padding:'8px 20px', fontSize:14, fontWeight:700, color:'#fff', marginBottom:40, marginTop:8, letterSpacing:1 }}>
          {task.ref_code}
        </div>
        <button onClick={onSaved} style={{ width:'100%', maxWidth:280, padding:'16px', fontSize:17, fontWeight:700, background:'#fff', color:'#16a34a', border:'none', borderRadius:14, cursor:'pointer', boxShadow:'0 4px 20px rgba(0,0,0,0.15)' }}>
          Done
        </button>
        <style>{`@keyframes popIn { from { transform:scale(0.4); opacity:0 } to { transform:scale(1); opacity:1 } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'#f5f7fa', zIndex:200, overflowY:'auto', fontFamily:'Inter,-apple-system,sans-serif' }}>
      <Flash/>

      {/* Header */}
      <div style={{ background:'var(--navy)', color:'#fff', padding:'14px 16px', display:'flex', alignItems:'center', gap:12, position:'sticky', top:0, zIndex:10 }}>
        <button onClick={onBack} style={{ background:'transparent', border:'none', color:'#fff', fontSize:26, cursor:'pointer', padding:'0 4px', lineHeight:1 }}>‹</button>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:15 }}>{task.ref_code}</div>
          <div style={{ fontSize:11, opacity:0.75, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{task.task_detail}</div>
        </div>
        <span style={{ fontSize:11, fontWeight:700, background:STATUS_COLOR[status]+'33', color:STATUS_COLOR[status], border:`1px solid ${STATUS_COLOR[status]}55`, borderRadius:20, padding:'3px 10px', whiteSpace:'nowrap' }}>
          {status.replace('_',' ').toUpperCase()}
        </span>
      </div>

      <div style={{ padding:16, paddingBottom:32 }}>
        {/* Customer + location */}
        <CustomerCard task={task} show={show}/>

        {/* Complaint details */}
        <div style={card}>
          <div style={secLabel}>COMPLAINT DETAILS</div>
          <div style={{ fontSize:14, fontWeight:600, color:'#1a2744', marginBottom:6 }}>{task.task_detail}</div>
          {task.description && <div style={{ fontSize:13, color:'#555', lineHeight:1.6, marginBottom:8 }}>{task.description}</div>}
          {task.priority && task.priority !== 'normal' && (
            <div style={{ marginTop:4, marginBottom:8 }}>
              <span style={{ fontSize:11, fontWeight:700, color:PRIORITY_COLOR[task.priority], background:PRIORITY_COLOR[task.priority]+'22', borderRadius:20, padding:'3px 9px' }}>
                {task.priority.toUpperCase()} PRIORITY
              </span>
            </div>
          )}
          {task.schedule_date && (
            <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#888', marginBottom:12 }}>
              <Calendar s={13}/> Scheduled: {task.schedule_date}
            </div>
          )}
          <div style={{ borderTop:'1px solid #f0f0f0', paddingTop:12, marginTop:4 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'#555', marginBottom:8 }}>Charge Breakdown</div>
            {chargeItems.map((item, idx) => (
              <div key={idx} style={{ display:'flex', gap:6, marginBottom:8, alignItems:'center' }}>
                <select
                  value={item.type}
                  onChange={e => setChargeItems(prev => prev.map((r,i) => i===idx ? {...r, type:e.target.value} : r))}
                  style={{ flex:1, border:'1.5px solid #ddd', borderRadius:8, padding:'9px 8px', fontSize:13, background:'#fff', boxSizing:'border-box' }}
                >
                  <option>Service Charge</option>
                  <option>Electrical Items</option>
                  <option>Fuel Charges</option>
                </select>
                <input
                  type="number" min="0" step="0.01" placeholder="0.00"
                  value={item.amount}
                  onChange={e => setChargeItems(prev => prev.map((r,i) => i===idx ? {...r, amount:e.target.value} : r))}
                  style={{ width:100, border:'1.5px solid #ddd', borderRadius:8, padding:'9px 10px', fontSize:14, boxSizing:'border-box' }}
                />
                {chargeItems.length > 1 && (
                  <button
                    onClick={() => setChargeItems(prev => prev.filter((_,i) => i!==idx))}
                    style={{ background:'#fee2e2', border:'none', borderRadius:8, width:32, height:36, color:'#dc2626', fontSize:18, cursor:'pointer', flexShrink:0 }}
                  >×</button>
                )}
              </div>
            ))}
            <button
              onClick={() => setChargeItems(prev => [...prev, { type:'Service Charge', amount:'' }])}
              style={{ width:'100%', padding:'8px', fontSize:12, fontWeight:600, background:'#f0f4ff', color:'#6366f1', border:'1.5px dashed #a5b4fc', borderRadius:8, cursor:'pointer', marginBottom:8 }}
            >+ Add Charge Item</button>
            {chargeItems.some(i => Number(i.amount) > 0) && (
              <div style={{ display:'flex', justifyContent:'space-between', background:'#f5f7fa', borderRadius:8, padding:'8px 12px', fontSize:13 }}>
                <span style={{ color:'#555', fontWeight:600 }}>Total Charges</span>
                <span style={{ fontWeight:800, color:'#1a2744' }}>
                  {fmtMoney(chargeItems.reduce((s,i) => s + (Number(i.amount)||0), 0))}
                </span>
              </div>
            )}
            {chargeItems.some(i => Number(i.amount) > 0) && amount && Number(amount) > 0 && (
              <div style={{ display:'flex', justifyContent:'space-between', background:'#f0fdf4', borderRadius:8, padding:'8px 12px', fontSize:13, marginTop:6 }}>
                <span style={{ color:'#555' }}>Balance Due</span>
                <span style={{ fontWeight:700, color: chargeItems.reduce((s,i) => s+(Number(i.amount)||0),0) > Number(amount) ? '#dc2626' : '#16a34a' }}>
                  {fmtMoney(Math.max(0, chargeItems.reduce((s,i) => s+(Number(i.amount)||0),0) - Number(amount)))}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Status */}
        <div style={card}>
          <div style={secLabel}>UPDATE STATUS</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {['open','in_progress','resolved'].map(s => (
              <button key={s} onClick={() => setStatus(s)} style={{
                padding:'9px 16px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer',
                border:`2px solid ${status===s ? STATUS_COLOR[s] : '#ddd'}`,
                background: status===s ? STATUS_COLOR[s]+'22' : '#fff',
                color: status===s ? STATUS_COLOR[s] : '#888',
              }}>
                {s === 'open' ? 'Open' : s === 'in_progress' ? 'In Progress' : 'Resolved'}
              </button>
            ))}
          </div>
        </div>

        {/* Remarks */}
        <div style={card}>
          <div style={secLabel}>REMARKS & NOTES</div>
          {task.resolution_notes && (
            <div style={{ background:'#f5f7fa', borderRadius:8, padding:12, marginBottom:12, fontSize:13, color:'#555', lineHeight:1.6, whiteSpace:'pre-wrap', maxHeight:120, overflowY:'auto' }}>
              {task.resolution_notes}
            </div>
          )}
          <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:6 }}>Add Remark</label>
          <textarea value={addRemark} onChange={e => setAddRemark(e.target.value)} placeholder="Type your remark here…" rows={3}
            style={{ width:'100%', border:'1.5px solid #ddd', borderRadius:8, padding:'10px', fontSize:14, resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }}/>
        </div>

        {/* Serial Number Photo */}
        <div style={{ ...card, borderLeft:'3px solid #6366f1' }}>
          <div style={secLabel}>SERIAL NUMBER PHOTO</div>
          <div style={{ fontSize:12, color:'#666', marginBottom:12, lineHeight:1.5 }}>
            If the inverter / product has been taken for servicing, capture a photo of its serial number label.
          </div>

          <input
            ref={serialPhotoInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display:'none' }}
            onChange={handleSerialPhotoCapture}
          />

          {serialPhoto ? (
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:'#16a34a', marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Serial number photo attached
              </div>
              <div style={{ position:'relative', display:'inline-block', width:'100%' }}>
                <img
                  src={serialPhoto}
                  alt="Serial number"
                  style={{ width:'100%', maxHeight:260, objectFit:'contain', borderRadius:10, border:'2px solid #6366f1', background:'#f5f5f5', display:'block' }}
                />
                <button
                  onClick={() => setSerialPhoto(null)}
                  style={{ position:'absolute', top:8, right:8, background:'rgba(0,0,0,0.6)', border:'none', borderRadius:'50%', width:28, height:28, color:'#fff', fontSize:16, cursor:'pointer', lineHeight:'28px', padding:0, textAlign:'center' }}
                >×</button>
              </div>
              <button
                onClick={() => serialPhotoInputRef.current?.click()}
                style={{ marginTop:10, width:'100%', padding:'10px', fontSize:13, fontWeight:600, background:'#ede9fe', color:'#6366f1', border:'1.5px solid #a5b4fc', borderRadius:10, cursor:'pointer' }}
              >
                Retake Photo
              </button>
            </div>
          ) : (
            <button
              onClick={() => serialPhotoInputRef.current?.click()}
              style={{ width:'100%', padding:'14px', fontSize:14, fontWeight:700, background:'#ede9fe', color:'#6366f1', border:'2px dashed #a5b4fc', borderRadius:12, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
              Take Photo of Serial Number
            </button>
          )}
        </div>

        {/* Payment */}
        <div style={card}>
          <div style={secLabel}>PAYMENT</div>
          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:6 }}>To Pay (₹)</label>
            <input type="number" min="0" step="0.01" placeholder="0.00"
              value={amount} onChange={e => setAmount(e.target.value)}
              style={{ width:'100%', border:'1.5px solid #ddd', borderRadius:8, padding:'10px 12px', fontSize:16, boxSizing:'border-box', background: chargeItems.some(i => Number(i.amount) > 0) ? '#f0fdf4' : '#fff' }}/>
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:6 }}>Payment Mode</label>
            <select value={payMode} onChange={e => setPayMode(e.target.value)}
              style={{ width:'100%', border:'1.5px solid #ddd', borderRadius:8, padding:'10px 12px', fontSize:14, background:'#fff', boxSizing:'border-box' }}>
              <option>Cash</option><option>UPI</option><option>Bank Transfer</option><option>Card</option>
            </select>
          </div>
          {upiQrUrl && (
            <div style={{ textAlign:'center', padding:'16px 0', borderTop:'1px solid #f0f0f0' }}>
              <div style={{ fontSize:13, fontWeight:600, color:'#555', marginBottom:10 }}>
                Scan to receive {fmtMoney(Number(amount))} via UPI
              </div>
              <img src={upiQrUrl} alt="UPI QR" style={{ width:200, height:200, borderRadius:12, border:'2px solid #ddd' }}/>
              {upiInfo?.upi_id && <div style={{ fontSize:12, color:'#888', marginTop:8 }}>UPI ID: {upiInfo.upi_id}</div>}
              <input ref={photoInputRef} type="file" accept="image/*" capture="environment" style={{ display:'none' }} onChange={handlePhotoCapture}/>
              {payPhoto ? (
                <div style={{ marginTop:16 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:'#555', marginBottom:8 }}>Payment Screenshot</div>
                  <div style={{ position:'relative', display:'inline-block' }}>
                    <img src={payPhoto} alt="Screenshot" style={{ width:'100%', maxHeight:220, objectFit:'contain', borderRadius:10, border:'2px solid #16a34a' }}/>
                    <button onClick={() => setPayPhoto(null)} style={{ position:'absolute', top:6, right:6, background:'rgba(0,0,0,0.55)', border:'none', borderRadius:'50%', width:26, height:26, color:'#fff', fontSize:15, cursor:'pointer', lineHeight:'26px', padding:0 }}>×</button>
                  </div>
                  <button onClick={() => confirmPayment(payPhoto)} style={{ marginTop:12, width:'100%', padding:'14px', fontSize:15, fontWeight:700, background:'#16a34a', color:'#fff', border:'none', borderRadius:12, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                    <Check s={18}/> Confirm Payment
                  </button>
                </div>
              ) : (
                <button onClick={() => photoInputRef.current?.click()} style={{ marginTop:18, width:'100%', padding:'14px', fontSize:15, fontWeight:700, background:'#f0fdf4', color:'#16a34a', border:'2px dashed #16a34a', borderRadius:12, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>
                  </svg>
                  Take Photo of Payment Screen
                </button>
              )}
            </div>
          )}
          {payMode === 'UPI' && !upiInfo?.upi_id && (
            <div style={{ fontSize:12, color:'#92400e', padding:'8px 12px', background:'#fef3c7', borderRadius:8, marginTop:8 }}>
              UPI ID not configured. Ask your admin to set it in Settings.
            </div>
          )}
        </div>

        <button onClick={save} disabled={saving}
          style={{ width:'100%', padding:'15px', fontSize:16, fontWeight:700, background:'var(--navy)', color:'#fff', border:'none', borderRadius:12, cursor:'pointer', opacity:saving?0.7:1, marginBottom:10 }}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>

        <button onClick={generateServiceBill}
          style={{ width:'100%', padding:'14px', fontSize:15, fontWeight:700, background:'#f0fdf4', color:'#16a34a', border:'2px solid #16a34a', borderRadius:12, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
          </svg>
          Generate Service Bill
        </button>
      </div>
    </div>
  );
}

// ── Service Detail ────────────────────────────────────────────────────────────

function ServiceDetail({ task, user, onBack, onSaved }) {
  const [status,       setStatus]       = useState(task.status || 'pending');
  const [addRemark,    setAddRemark]    = useState('');
  const [saving,       setSaving]       = useState(false);
  const { show, Flash } = useFlash();

  const save = async () => {
    setSaving(true);
    try {
      const now = nowIST();
      const ts = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
      const newNotes = addRemark.trim()
        ? (task.resolution_notes ? task.resolution_notes + '\n' : '') + `[${ts}] ${user.name}: ${addRemark.trim()}`
        : undefined;
      await servicesApi.update(task.id, {
        status,
        ...(newNotes !== undefined && { completion_notes: newNotes }),
      });
      show('Saved successfully!');
      onSaved();
    } catch (e) {
      show(parseErr(e, 'Failed to save.'), 'error');
    } finally { setSaving(false); }
  };

  const card = { background:'#fff', borderRadius:12, padding:16, marginBottom:12, boxShadow:'0 1px 4px rgba(0,0,0,0.08)' };
  const secLabel = { fontSize:11, fontWeight:700, color:'#888', marginBottom:10, letterSpacing:0.5 };

  return (
    <div style={{ position:'fixed', inset:0, background:'#f5f7fa', zIndex:200, overflowY:'auto', fontFamily:'Inter,-apple-system,sans-serif' }}>
      <Flash/>

      {/* Header */}
      <div style={{ background:'#6366f1', color:'#fff', padding:'14px 16px', display:'flex', alignItems:'center', gap:12, position:'sticky', top:0, zIndex:10 }}>
        <button onClick={onBack} style={{ background:'transparent', border:'none', color:'#fff', fontSize:26, cursor:'pointer', padding:'0 4px', lineHeight:1 }}>‹</button>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:15 }}>{task.ref_code}</div>
          <div style={{ fontSize:11, opacity:0.75, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{task.task_detail}</div>
        </div>
        <span style={{ fontSize:11, fontWeight:700, background:'rgba(255,255,255,0.2)', color:'#fff', border:'1px solid rgba(255,255,255,0.4)', borderRadius:20, padding:'3px 10px', whiteSpace:'nowrap' }}>
          {status.replace('_',' ').toUpperCase()}
        </span>
      </div>

      <div style={{ padding:16, paddingBottom:32 }}>
        {/* Customer + location */}
        <CustomerCard task={task} show={show}/>

        {/* Service details */}
        <div style={card}>
          <div style={secLabel}>SERVICE REQUEST</div>
          <div style={{ fontSize:14, fontWeight:600, color:'#1a2744', marginBottom:6 }}>{task.task_detail}</div>
          {task.description && <div style={{ fontSize:13, color:'#555', lineHeight:1.6 }}>{task.description}</div>}
          {task.schedule_date && (
            <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#888', marginTop:8 }}>
              <Calendar s={13}/> Scheduled: {task.schedule_date}
            </div>
          )}
        </div>

        {/* Status */}
        <div style={card}>
          <div style={secLabel}>UPDATE STATUS</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {[
              { val:'pending',     label:'Pending',     color:'#dc2626' },
              { val:'in_progress', label:'In Progress', color:'#d97706' },
              { val:'completed',   label:'Completed',   color:'#16a34a' },
            ].map(s => (
              <button key={s.val} onClick={() => setStatus(s.val)} style={{
                padding:'9px 16px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer',
                border:`2px solid ${status===s.val ? s.color : '#ddd'}`,
                background: status===s.val ? s.color+'22' : '#fff',
                color: status===s.val ? s.color : '#888',
              }}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Notes / remarks */}
        <div style={card}>
          <div style={secLabel}>COMPLETION NOTES</div>
          {task.resolution_notes && (
            <div style={{ background:'#f5f7fa', borderRadius:8, padding:12, marginBottom:12, fontSize:13, color:'#555', lineHeight:1.6, whiteSpace:'pre-wrap', maxHeight:120, overflowY:'auto' }}>
              {task.resolution_notes}
            </div>
          )}
          <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:6 }}>Add Note / Remark</label>
          <textarea value={addRemark} onChange={e => setAddRemark(e.target.value)} placeholder="Describe the work done or any observations…" rows={4}
            style={{ width:'100%', border:'1.5px solid #ddd', borderRadius:8, padding:'10px', fontSize:14, resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }}/>
        </div>

        <button onClick={save} disabled={saving}
          style={{ width:'100%', padding:'15px', fontSize:16, fontWeight:700, background:'#6366f1', color:'#fff', border:'none', borderRadius:12, cursor:'pointer', opacity:saving?0.7:1 }}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// ── Sale Detail ───────────────────────────────────────────────────────────────

function SaleDetail({ task, user, onBack, onSaved }) {
  const alreadyPaid = Number(task.sale_amount_paid) || 0;
  const total       = Number(task.sale_total) || 0;

  const [payStatus,     setPayStatus]     = useState(task.sale_payment_status || task.status || 'pending');
  const [partialAmt,    setPartialAmt]    = useState('');
  const [payMode,       setPayMode]       = useState(task.payment_mode || 'Cash');
  const [followupDate,  setFollowupDate]  = useState(task.schedule_date ? String(task.schedule_date) : '');
  const [saving,        setSaving]        = useState(false);
  const [upiInfo,       setUpiInfo]       = useState(null);
  const { show, Flash } = useFlash();

  const saleItems = (() => {
    try { return task.sale_items ? JSON.parse(task.sale_items) : []; }
    catch { return []; }
  })();

  // Amount being collected right now
  const collectingNow = payStatus === 'partial'
    ? (Number(partialAmt) || 0)
    : payStatus === 'paid'
    ? Math.max(0, total - alreadyPaid)
    : 0;

  const newTotalPaid = alreadyPaid + collectingNow;
  const balance      = Math.max(0, total - newTotalPaid);

  useEffect(() => {
    settingsApi.get().then(({ data }) => setUpiInfo(data)).catch(() => {});
  }, []);

  // QR uses the amount being collected right now; if UPI selected and collecting > 0
  const upiAmount = collectingNow > 0 ? collectingNow : Math.max(0, total - alreadyPaid);
  const upiQrUrl  = (() => {
    if (payMode !== 'UPI' || !upiInfo?.upi_id || upiAmount <= 0) return null;
    const upiStr = `upi://pay?pa=${upiInfo.upi_id}&pn=${encodeURIComponent(upiInfo.upi_name || 'Sakthi Solar')}&am=${upiAmount.toFixed(2)}&cu=INR&tn=${task.ref_code}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(upiStr)}`;
  })();

  const save = async () => {
    if (payStatus === 'partial' && (!partialAmt || Number(partialAmt) <= 0)) {
      show('Enter the partial amount received.', 'error'); return;
    }
    setSaving(true);
    try {
      const payload = {
        payment_status: payStatus,
        amount_paid: newTotalPaid,
        ...(payMode && { payment_mode: payMode }),
        ...(followupDate && payStatus !== 'paid' && { followup_date: followupDate }),
      };
      await salesApi.update(task.id, payload);
      show('Saved successfully!');
      onSaved();
    } catch (e) {
      show('Failed to save.', 'error');
    } finally { setSaving(false); }
  };

  const card     = { background:'#fff', borderRadius:12, padding:16, marginBottom:12, boxShadow:'0 1px 4px rgba(0,0,0,0.08)' };
  const secLabel = { fontSize:11, fontWeight:700, color:'#888', marginBottom:10, letterSpacing:0.5 };

  return (
    <div style={{ position:'fixed', inset:0, background:'#f5f7fa', zIndex:200, overflowY:'auto', fontFamily:'Inter,-apple-system,sans-serif' }}>
      <Flash/>

      {/* Header */}
      <div style={{ background:'#0ea5e9', color:'#fff', padding:'14px 16px', display:'flex', alignItems:'center', gap:12, position:'sticky', top:0, zIndex:10 }}>
        <button onClick={onBack} style={{ background:'transparent', border:'none', color:'#fff', fontSize:26, cursor:'pointer', padding:'0 4px', lineHeight:1 }}>‹</button>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:15 }}>{task.ref_code}</div>
          <div style={{ fontSize:11, opacity:0.75 }}>{task.task_detail} · {saleItems.length} item{saleItems.length !== 1 ? 's' : ''}</div>
        </div>
        <span style={{ fontSize:11, fontWeight:700, background:'rgba(255,255,255,0.2)', color:'#fff', border:'1px solid rgba(255,255,255,0.4)', borderRadius:20, padding:'3px 10px', whiteSpace:'nowrap' }}>
          {payStatus.toUpperCase()}
        </span>
      </div>

      <div style={{ padding:16, paddingBottom:32 }}>
        {/* Customer */}
        <CustomerCard task={task} show={show}/>

        {/* Sale Items */}
        <div style={card}>
          <div style={secLabel}>SALE ITEMS</div>
          {saleItems.length === 0
            ? <div style={{ fontSize:13, color:'#aaa' }}>No items</div>
            : saleItems.map((item, idx) => (
              <div key={idx} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', paddingBottom:10, marginBottom:10, borderBottom: idx < saleItems.length-1 ? '1px solid #f0f0f0' : 'none' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:'#1a2744' }}>{item.product_name}</div>
                  <div style={{ fontSize:11, color:'#888', marginTop:2 }}>Qty: {item.qty} × {fmtMoney(item.unit_price)}</div>
                  {item.serial_number && <div style={{ fontSize:11, color:'#6366f1', marginTop:2 }}>S/N: {item.serial_number}</div>}
                </div>
                <div style={{ fontWeight:700, fontSize:14, color:'#1a2744', marginLeft:12 }}>{fmtMoney(item.line_total)}</div>
              </div>
            ))
          }
          {/* Summary row */}
          <div style={{ borderTop:'2px solid #1a2744', marginTop:4, paddingTop:10, display:'flex', flexDirection:'column', gap:4 }}>
            <div style={{ display:'flex', justifyContent:'space-between' }}>
              <span style={{ fontWeight:800, fontSize:14, color:'#1a2744' }}>Total</span>
              <span style={{ fontWeight:800, fontSize:16, color:'#1a2744' }}>{fmtMoney(total)}</span>
            </div>
            {alreadyPaid > 0 && (
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontSize:12, color:'#16a34a' }}>Already Paid</span>
                <span style={{ fontSize:12, fontWeight:700, color:'#16a34a' }}>{fmtMoney(alreadyPaid)}</span>
              </div>
            )}
            <div style={{ display:'flex', justifyContent:'space-between', background:'#fff7ed', borderRadius:6, padding:'6px 8px', marginTop:4 }}>
              <span style={{ fontSize:13, fontWeight:700, color:'#92400e' }}>Balance Due</span>
              <span style={{ fontSize:14, fontWeight:800, color:'#dc2626' }}>{fmtMoney(Math.max(0, total - alreadyPaid))}</span>
            </div>
          </div>
        </div>

        {/* Payment */}
        <div style={card}>
          <div style={secLabel}>PAYMENT</div>

          {/* 1 — Payment Status */}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:8 }}>Payment Status</label>
            <div style={{ display:'flex', gap:8 }}>
              {[{v:'pending',label:'Pending',c:'#dc2626'},{v:'partial',label:'Partial',c:'#d97706'},{v:'paid',label:'Paid',c:'#16a34a'}].map(s => (
                <button key={s.v} onClick={() => { setPayStatus(s.v); setPartialAmt(''); }} style={{
                  flex:1, padding:'10px 4px', borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer',
                  border:`2px solid ${payStatus===s.v ? s.c : '#ddd'}`,
                  background: payStatus===s.v ? s.c+'22' : '#fff',
                  color: payStatus===s.v ? s.c : '#888',
                }}>{s.label}</button>
              ))}
            </div>
          </div>

          {/* 2 — To Pay (auto-filled for paid, input for partial) */}
          {payStatus === 'partial' && (
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:6 }}>Amount Received Now (₹)</label>
              <input type="number" min="0" step="0.01"
                placeholder={`Up to ${fmtMoney(Math.max(0, total - alreadyPaid))}`}
                value={partialAmt} onChange={e => setPartialAmt(e.target.value)}
                style={{ width:'100%', border:'1.5px solid #d97706', borderRadius:8, padding:'10px 12px', fontSize:16, boxSizing:'border-box', background:'#fffbeb' }}/>
            </div>
          )}

          {payStatus === 'paid' && (
            <div style={{ marginBottom:14, background:'#f0fdf4', borderRadius:8, padding:'10px 14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:13, color:'#555' }}>To Pay (Full Balance)</span>
              <span style={{ fontWeight:800, fontSize:16, color:'#16a34a' }}>{fmtMoney(Math.max(0, total - alreadyPaid))}</span>
            </div>
          )}

          {/* 3 — Payment Mode */}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:6 }}>Payment Mode</label>
            <select value={payMode} onChange={e => setPayMode(e.target.value)}
              style={{ width:'100%', border:'1.5px solid #ddd', borderRadius:8, padding:'10px 12px', fontSize:14, background:'#fff', boxSizing:'border-box' }}>
              <option>Cash</option><option>UPI</option><option>Bank Transfer</option><option>Card</option>
            </select>
          </div>

          {/* 4 — UPI QR */}
          {payMode === 'UPI' && (
            <div style={{ textAlign:'center', padding:'16px 0', borderTop:'1px solid #f0f0f0', marginBottom:14 }}>
              {upiQrUrl ? (
                <>
                  <div style={{ fontSize:13, fontWeight:600, color:'#555', marginBottom:10 }}>
                    Scan to receive {fmtMoney(upiAmount)} via UPI
                  </div>
                  <img src={upiQrUrl} alt="UPI QR" style={{ width:210, height:210, borderRadius:12, border:'2px solid #0ea5e9' }}/>
                  {upiInfo?.upi_id && <div style={{ fontSize:12, color:'#888', marginTop:8 }}>UPI ID: {upiInfo.upi_id}</div>}
                </>
              ) : (
                <div style={{ fontSize:12, color:'#92400e', padding:'8px 12px', background:'#fef3c7', borderRadius:8 }}>
                  UPI ID not configured. Ask your admin to set it in Settings.
                </div>
              )}
            </div>
          )}

          {/* 5 — Balance after this transaction */}
          {(payStatus === 'partial' || payStatus === 'paid') && (
            <div style={{ display:'flex', justifyContent:'space-between', background: balance===0 ? '#f0fdf4' : '#fff7ed', borderRadius:8, padding:'10px 14px', marginBottom:14 }}>
              <span style={{ fontSize:13, color:'#555' }}>Remaining Balance After</span>
              <span style={{ fontWeight:800, fontSize:15, color: balance===0 ? '#16a34a' : '#dc2626' }}>{fmtMoney(balance)}</span>
            </div>
          )}

          {/* 6 — Follow-up date (pending / partial only) */}
          {payStatus !== 'paid' && (
            <div style={{ borderTop:'1px solid #f0f0f0', paddingTop:14 }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:6 }}>
                Follow-up Date
              </label>
              <input type="date" value={followupDate} onChange={e => setFollowupDate(e.target.value)}
                min={todayStr()}
                style={{ width:'100%', border:'1.5px solid #ddd', borderRadius:8, padding:'10px 12px', fontSize:14, boxSizing:'border-box', background:'#fff' }}/>
              {followupDate && (
                <div style={{ fontSize:11, color:'#6366f1', marginTop:6 }}>
                  Follow-up set for {new Date(followupDate + 'T00:00:00').toLocaleDateString('en-IN', { dateStyle:'medium' })} — visible in Payment FUPs
                </div>
              )}
            </div>
          )}
        </div>

        <button onClick={save} disabled={saving}
          style={{ width:'100%', padding:'15px', fontSize:16, fontWeight:700, background:'#0ea5e9', color:'#fff', border:'none', borderRadius:12, cursor:'pointer', opacity:saving?0.7:1 }}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// ── My Tasks (Complaints + Services + Sales) ──────────────────────────────────

function MyTasks({ tasks, loading, onRefresh, onSelectTask, onClearNew }) {
  const [filter, setFilter] = useState('all');
  useEffect(() => { onClearNew(); }, [onClearNew]);

  const complaints = tasks.filter(t => t.type === 'complaint');
  const services   = tasks.filter(t => t.type === 'service');
  const sales      = tasks.filter(t => t.type === 'sale');
  const visible    = filter === 'all' ? tasks : tasks.filter(t => t.type === filter);

  if (loading) return <div style={{ padding:40, textAlign:'center' }}><Spin label="Loading tasks…"/></div>;

  return (
    <div style={{ padding:16 }}>
      {/* Stats — 2×2 + 1 row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:8 }}>
        {[
          { label:'Total',      val: tasks.length,       color:'var(--navy)' },
          { label:'Complaints', val: complaints.length,  color:'#dc2626'     },
          { label:'Services',   val: services.length,    color:'#6366f1'     },
          { label:'Sales',      val: sales.length,       color:'#0ea5e9'     },
        ].map(s => (
          <div key={s.label} style={{ background:'#fff', borderRadius:10, padding:'12px 6px', textAlign:'center', boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
            <div style={{ fontSize:20, fontWeight:800, color:s.color }}>{s.val}</div>
            <div style={{ fontSize:10, color:'#888', marginTop:2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ background:'#fff', borderRadius:10, padding:'10px 14px', marginBottom:16, boxShadow:'0 1px 3px rgba(0,0,0,0.08)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <span style={{ fontSize:12, color:'#888' }}>Open / Pending</span>
        <span style={{ fontSize:18, fontWeight:800, color:'#d97706' }}>
          {tasks.filter(r => r.status==='open'||r.status==='pending').length}
        </span>
      </div>

      {/* Filter pills */}
      <div style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'wrap' }}>
        {[
          { id:'all',       label:'All Tasks' },
          { id:'complaint', label:`Complaints (${complaints.length})` },
          { id:'service',   label:`Services (${services.length})` },
          { id:'sale',      label:`Sales (${sales.length})` },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            style={{
              padding:'6px 14px', fontSize:12, fontWeight:600, borderRadius:20, cursor:'pointer',
              border:`1.5px solid ${filter===f.id ? 'var(--navy)' : '#ddd'}`,
              background: filter===f.id ? 'var(--navy)' : '#fff',
              color: filter===f.id ? '#fff' : '#666',
            }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Refresh */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <span style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#888' }}>
          <span style={{ width:8, height:8, borderRadius:'50%', background:'#16a34a', display:'inline-block' }}/>
          Live — updates every 60s
        </span>
        <button onClick={onRefresh} style={{ fontSize:12, padding:'5px 12px', borderRadius:6, border:'1px solid #ddd', background:'#fff', cursor:'pointer' }}>
          ↻ Refresh
        </button>
      </div>

      {visible.length === 0 ? (
        <div style={{ textAlign:'center', padding:48, color:'#aaa', fontSize:14 }}>
          <div style={{ marginBottom:12, display:'flex', justifyContent:'center' }}>
            <ClipCheck s={40} style={{ color:'#ccc' }}/>
          </div>
          {filter === 'all' ? 'No tasks assigned to you right now'
            : filter === 'complaint' ? 'No complaints assigned'
            : filter === 'service'   ? 'No service requests assigned'
            : 'No sales assigned'}
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {visible.map(task => {
            const isSale       = task.type === 'sale';
            const isService    = task.type === 'service';
            const accentColor  = isSale ? '#0ea5e9' : isService ? '#6366f1' : (STATUS_COLOR[task.status] || '#ccc');
            const statusLabel  = task.status.replace('_',' ').toUpperCase();
            const typeLabel    = isSale ? 'SALE' : isService ? 'SERVICE' : 'COMPLAINT';
            const typeBg       = isSale ? '#e0f2fe' : isService ? '#ede9fe' : '#fee2e2';
            const typeColor    = isSale ? '#0ea5e9' : isService ? '#6366f1' : '#dc2626';
            return (
              <div key={task.id} onClick={() => onSelectTask(task)}
                style={{ background:'#fff', borderRadius:12, padding:'14px 16px', borderLeft:`4px solid ${accentColor}`, boxShadow:'0 1px 4px rgba(0,0,0,0.08)', cursor:'pointer', WebkitTapHighlightColor:'transparent' }}>
                {/* Header row */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontFamily:'monospace', fontSize:13, fontWeight:700, color:'#1a2744' }}>{task.ref_code}</span>
                    <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:10, background:typeBg, color:typeColor }}>
                      {typeLabel}
                    </span>
                  </div>
                  <span style={{ fontSize:11, fontWeight:700, color:accentColor, background:accentColor+'22', borderRadius:20, padding:'2px 8px' }}>
                    {statusLabel}
                  </span>
                </div>

                <div style={{ fontSize:14, fontWeight:600, color:'#1a2744', marginBottom:4 }}>{task.task_detail}</div>
                {task.description && (
                  <div style={{ fontSize:12, color:'#666', marginBottom:8, lineHeight:1.4 }}>
                    {task.description.slice(0,80)}{task.description.length>80?'…':''}
                  </div>
                )}

                {/* Customer info block */}
                <div style={{ background:'#f8f9fb', borderRadius:8, padding:'10px 12px', marginTop:8 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:'#1a2744', marginBottom:6 }}>
                    {task.customer_name !== '—' ? task.customer_name : (task.walk_in_name||'—')}
                  </div>
                  {(task.customer_phone||task.walk_in_phone) && (
                    <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#374151', marginBottom:4 }}>
                      <Phone s={12} style={{ flexShrink:0 }}/> {task.customer_phone||task.walk_in_phone}
                    </div>
                  )}
                  {task.customer_address && (
                    <div style={{ display:'flex', alignItems:'flex-start', gap:6, fontSize:12, color:'#374151' }}>
                      <MapPin s={12} style={{ flexShrink:0, marginTop:1 }}/> <span style={{ lineHeight:1.4 }}>{task.customer_address.slice(0,80)}{task.customer_address.length>80?'…':''}</span>
                    </div>
                  )}
                </div>

                {task.priority && task.priority !== 'normal' && (
                  <div style={{ marginTop:8 }}>
                    <span style={{ fontSize:10, fontWeight:700, color:PRIORITY_COLOR[task.priority], background:PRIORITY_COLOR[task.priority]+'22', borderRadius:10, padding:'2px 7px' }}>
                      {task.priority.toUpperCase()}
                    </span>
                  </div>
                )}
                {task.schedule_date && (
                  <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, color:'#aaa', marginTop:6 }}>
                    <Calendar s={11}/> Scheduled: {task.schedule_date}
                  </div>
                )}
                <div style={{ marginTop:8, fontSize:11, color:'#bbb', textAlign:'right' }}>Tap to open →</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── My Attendance ─────────────────────────────────────────────────────────────

const ATT_STYLE = {
  present:  { color:'#16a34a', bg:'rgba(22,163,74,0.12)',  label:'P'   },
  half_day: { color:'#b45309', bg:'rgba(245,158,11,0.12)', label:'HD'  },
  absent:   { color:'#dc2626', bg:'rgba(220,53,69,0.10)',  label:'A'   },
  off:      { color:'#7c3aed', bg:'rgba(124,58,237,0.10)', label:'Off' },
  holiday:  { color:'#7c3aed', bg:'rgba(124,58,237,0.10)', label:'Off' },
};

function MyAttendance() {
  const now   = nowIST();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year,  setYear]  = useState(now.getFullYear());
  const [data,  setData]  = useState([]);
  const [busy,  setBusy]  = useState(false);

  const load = useCallback(async (m, y) => {
    setBusy(true);
    try {
      const { data: rows } = await fieldApi.myAttendance({ month:m, year:y });
      setData(rows);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(month, year); }, [month, year, load]);

  const byDate = {};
  data.forEach(a => { byDate[a.date] = a; });

  const firstDay    = new Date(year, month-1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const today  = todayStr();
  const counts = { present:0, half_day:0, absent:0, off:0, holiday:0 };
  data.forEach(a => { if (counts[a.status] !== undefined) counts[a.status]++; });
  const workDays    = data.filter(a => a.status !== 'off' && a.status !== 'holiday').length;
  const presentDays = data.filter(a => a.status === 'present').length;
  const pct = workDays > 0 ? Math.round((presentDays / workDays) * 100) : null;

  const yearOpts = [];
  for (let y = now.getFullYear(); y >= now.getFullYear()-2; y--) yearOpts.push(y);

  return (
    <div style={{ padding:16 }}>
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        <select value={month} onChange={e => setMonth(Number(e.target.value))}
          style={{ flex:1, height:40, border:'1.5px solid #ddd', borderRadius:8, fontSize:14, padding:'0 8px', background:'#fff' }}>
          {MNF.map((n,i) => <option key={i} value={i+1}>{n}</option>)}
        </select>
        <select value={year} onChange={e => setYear(Number(e.target.value))}
          style={{ width:90, height:40, border:'1.5px solid #ddd', borderRadius:8, fontSize:14, padding:'0 8px', background:'#fff' }}>
          {yearOpts.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {busy && <Spin/>}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:16 }}>
        <div style={{ background:'#fff', borderRadius:10, padding:'10px 6px', textAlign:'center', boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize:22, fontWeight:800, color:'#16a34a' }}>{counts.present}</div>
          <div style={{ fontSize:10, color:'#888', marginTop:2 }}>Present</div>
        </div>
        <div style={{ background:'#fff', borderRadius:10, padding:'10px 6px', textAlign:'center', boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize:22, fontWeight:800, color:'#b45309' }}>{counts.half_day}</div>
          <div style={{ fontSize:10, color:'#888', marginTop:2 }}>Half Day</div>
        </div>
        <div style={{ background:'#fff', borderRadius:10, padding:'10px 6px', textAlign:'center', boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize:22, fontWeight:800, color:'#dc2626' }}>{counts.absent}</div>
          <div style={{ fontSize:10, color:'#888', marginTop:2 }}>Absent</div>
        </div>
        <div style={{ background:'#fff', borderRadius:10, padding:'10px 6px', textAlign:'center', boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize:18, fontWeight:800, color: pct!==null && pct<75 ? '#dc2626':'#16a34a' }}>
            {pct!==null ? `${pct}%` : '—'}
          </div>
          <div style={{ fontSize:10, color:'#888', marginTop:2 }}>Attendance</div>
        </div>
      </div>

      {pct !== null && pct < 75 && (
        <div style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:10, padding:'10px 14px', marginBottom:16, fontSize:13, color:'#92400e', display:'flex', alignItems:'center', gap:8 }}>
          <AlertTri s={16}/> Attendance below 75% ({pct}%). Please speak to your manager.
        </div>
      )}

      <div style={{ background:'#fff', borderRadius:12, padding:14, boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, textAlign:'center' }}>
          {DS.map(d => (
            <div key={d} style={{ fontSize:10, fontWeight:700, color:'#bbb', padding:'6px 0', textTransform:'uppercase' }}>{d}</div>
          ))}
          {cells.map((day, i) => {
            if (!day) return <div key={`e-${i}`}/>;
            const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
            const isSun   = new Date(dateStr+'T00:00:00').getDay() === 0;
            const att     = byDate[dateStr];
            const isToday = dateStr === today;
            const ss      = att ? (ATT_STYLE[att.status]||{}) : {};
            return (
              <div key={day} style={{
                borderRadius:6, padding:'8px 2px', fontSize:13, fontWeight:isToday?700:400,
                background: att?ss.bg : isSun?'rgba(0,0,0,0.03)':'transparent',
                color: att?ss.color : isSun?'#bbb':'#333',
                border: isToday?'2px solid var(--navy)':'1px solid transparent',
                display:'flex', flexDirection:'column', alignItems:'center', gap:2,
              }}>
                <span>{day}</span>
                {att  && <span style={{ fontSize:8, fontWeight:700 }}>{ss.label}</span>}
                {!att && isSun && <span style={{ fontSize:8 }}>OFF</span>}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display:'flex', gap:12, marginTop:12, flexWrap:'wrap' }}>
        {Object.entries(ATT_STYLE).filter(([k]) => k!=='holiday').map(([k,s]) => (
          <div key={k} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11 }}>
            <span style={{ width:10, height:10, borderRadius:2, background:s.bg, border:`1.5px solid ${s.color}`, display:'inline-block' }}/>
            <span style={{ color:'#666' }}>{s.label==='P'?'Present':s.label==='HD'?'Half Day':s.label==='A'?'Absent':'Day Off'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Salary Ack ────────────────────────────────────────────────────────────────

function SalaryAck({ user }) {
  const now          = nowIST();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [myAcks,   setMyAcks]   = useState([]);
  const [amount,   setAmount]   = useState('');
  const [notes,    setNotes]    = useState('');
  const [submitting, setSub]    = useState(false);
  const [busy,     setBusy]     = useState(true);
  const [viewSig,  setViewSig]  = useState(null);
  const sigRef = useRef(null);
  const { show, Flash } = useFlash();

  const loadAcks = useCallback(async () => {
    try {
      const { data } = await salaryAckApi.my();
      setMyAcks(data);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { loadAcks(); }, [loadAcks]);

  const existingForMonth = myAcks.find(a => a.month === selectedMonth);

  const submit = async () => {
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      show('Please enter a valid amount received.', 'error'); return;
    }
    if (sigRef.current?.isEmpty()) {
      show('Please draw your signature before submitting.', 'error'); return;
    }
    const signature_data = sigRef.current.getDataURL();
    setSub(true);
    try {
      await salaryAckApi.submit({ month:selectedMonth, amount_received:Number(amount), signature_data, notes:notes.trim()||null });
      show('Salary acknowledgement submitted!');
      setAmount(''); setNotes('');
      sigRef.current?.clear();
      loadAcks();
    } catch (e) {
      show(parseErr(e, 'Failed to submit.'), 'error');
    } finally { setSub(false); }
  };

  const monthOptions = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth()-i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    monthOptions.push({ val, lbl: d.toLocaleDateString('en-IN',{month:'long',year:'numeric'}) });
  }

  if (busy) return <div style={{ padding:40, textAlign:'center' }}><Spin label="Loading…"/></div>;

  const card = { background:'#fff', borderRadius:12, padding:16, marginBottom:12, boxShadow:'0 1px 4px rgba(0,0,0,0.08)' };

  return (
    <div style={{ padding:16 }}>
      <Flash/>
      {viewSig && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }} onClick={() => setViewSig(null)}>
          <div style={{ background:'#fff', borderRadius:16, padding:20, maxWidth:500, width:'100%' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight:700, marginBottom:8 }}>Signature — {monthOptions.find(m=>m.val===viewSig.month)?.lbl||viewSig.month}</div>
            <div style={{ fontSize:13, color:'#666', marginBottom:12 }}>
              Amount: {fmtMoney(viewSig.amount_received)} · {new Date(viewSig.acknowledged_at).toLocaleDateString('en-IN',{dateStyle:'medium'})}
            </div>
            {viewSig.notes && <div style={{ fontSize:12, color:'#888', marginBottom:10 }}>Notes: {viewSig.notes}</div>}
            <img src={viewSig.signature_data} alt="Signature" style={{ width:'100%', border:'1px solid #ddd', borderRadius:8, background:'#fafbfc' }}/>
            <button onClick={() => setViewSig(null)} style={{ width:'100%', marginTop:14, padding:'10px', borderRadius:8, border:'1.5px solid #ddd', background:'#fff', fontSize:14, cursor:'pointer' }}>Close</button>
          </div>
        </div>
      )}

      <div style={{ marginBottom:16 }}>
        <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#555', marginBottom:6 }}>Select Month</label>
        <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
          style={{ width:'100%', height:42, border:'1.5px solid #ddd', borderRadius:8, fontSize:14, padding:'0 12px', background:'#fff' }}>
          {monthOptions.map(m => <option key={m.val} value={m.val}>{m.lbl}</option>)}
        </select>
      </div>

      {existingForMonth ? (
        <div>
          <div style={{ ...card, border:'1.5px solid #16a34a', background:'#f0fdf4' }}>
            <div style={{ fontWeight:700, color:'#16a34a', marginBottom:4, display:'flex', alignItems:'center', gap:6 }}>
              <Check s={16}/> Already submitted for {monthOptions.find(m=>m.val===selectedMonth)?.lbl||selectedMonth}
            </div>
            <div style={{ fontSize:13, color:'#555' }}>
              Amount: {fmtMoney(existingForMonth.amount_received)} · Submitted {new Date(existingForMonth.acknowledged_at).toLocaleDateString('en-IN',{dateStyle:'medium'})} at{' '}
              {new Date(existingForMonth.acknowledged_at).toLocaleTimeString('en-IN',{timeStyle:'short',hour12:true})}
            </div>
            <button className="btn btn-sm" style={{ marginTop:12 }} onClick={() => setViewSig(existingForMonth)}>View Signature</button>
          </div>
        </div>
      ) : (
        <div>
          <div style={card}>
            <div style={{ fontSize:14, fontWeight:600, marginBottom:16, color:'#1a2744' }}>
              Acknowledge salary for {monthOptions.find(m=>m.val===selectedMonth)?.lbl||selectedMonth}
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:6 }}>Amount Received (₹) *</label>
              <input type="number" min="0" step="0.01" placeholder="0.00"
                value={amount} onChange={e => setAmount(e.target.value)}
                style={{ width:'100%', border:'1.5px solid #ddd', borderRadius:8, padding:'10px 12px', fontSize:16, boxSizing:'border-box' }}/>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:6 }}>Notes (optional)</label>
              <input type="text" placeholder="Any remarks…"
                value={notes} onChange={e => setNotes(e.target.value)}
                style={{ width:'100%', border:'1.5px solid #ddd', borderRadius:8, padding:'10px 12px', fontSize:14, boxSizing:'border-box' }}/>
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:6 }}>
                Signature * <span style={{ fontWeight:400, color:'#aaa' }}>— draw below</span>
              </label>
              <SignaturePad ref={sigRef}/>
              <button onClick={() => sigRef.current?.clear()}
                style={{ marginTop:6, fontSize:12, padding:'5px 12px', borderRadius:6, border:'1px solid #ddd', background:'#fff', cursor:'pointer' }}>
                Clear Signature
              </button>
            </div>
          </div>
          <button onClick={submit} disabled={submitting}
            style={{ width:'100%', padding:'15px', fontSize:16, fontWeight:700, background:'var(--navy)', color:'#fff', border:'none', borderRadius:12, cursor:'pointer', opacity:submitting?0.7:1 }}>
            {submitting ? 'Submitting…' : 'Submit Acknowledgement'}
          </button>
          <div style={{ textAlign:'center', fontSize:11, color:'#aaa', marginTop:10 }}>
            I confirm I have received the stated salary for the selected month.
          </div>
        </div>
      )}

      {myAcks.length > 0 && (
        <div style={{ marginTop:24 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'#555', marginBottom:10 }}>Past Acknowledgements</div>
          {myAcks.map(a => (
            <div key={a.id} style={{ ...card, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
              <div>
                <div style={{ fontWeight:600, fontSize:13, color:'#1a2744' }}>
                  {monthOptions.find(m=>m.val===a.month)?.lbl || a.month}
                </div>
                <div style={{ fontSize:13, fontWeight:700, color:'#16a34a' }}>{fmtMoney(a.amount_received)}</div>
                <div style={{ fontSize:11, color:'#aaa', marginTop:2 }}>
                  {new Date(a.acknowledged_at).toLocaleDateString('en-IN',{dateStyle:'medium'})}
                </div>
              </div>
              <button onClick={() => setViewSig(a)}
                style={{ fontSize:12, padding:'6px 14px', borderRadius:8, border:'1.5px solid #ddd', background:'#fff', cursor:'pointer', whiteSpace:'nowrap' }}>
                View Signature
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function FieldPortal() {
  const { user, logout }       = useAuth();
  const navigate               = useNavigate();
  const [tab, setTab]          = useState('tasks');
  const [tasks, setTasks]      = useState([]);
  const [loading, setLoading]  = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newCount, setNewCount]         = useState(0);

  const fetchTasks = useCallback(async (first = false) => {
    if (first) setLoading(true);
    try {
      const { data } = await assignmentsApi.list();
      const allTasks = data.filter(d => {
        if (d.type === 'sale')      return d.sale_payment_status === 'pending';
        if (d.type === 'complaint') return d.status === 'open' || d.status === 'in_progress';
        if (d.type === 'service')   return d.status === 'pending' || d.status === 'in_progress';
        return false;
      });
      setTasks(allTasks);

      if (!first) {
        const seen    = getSeenIds();
        const newOnes = allTasks.filter(c => !seen.has(c.id));
        if (newOnes.length > 0) {
          setNewCount(prev => prev + newOnes.length);
          playBeep();
          newOnes.forEach(t => seen.add(t.id));
          saveSeenIds(seen);
        }
      } else {
        saveSeenIds(new Set(allTasks.map(c => c.id)));
      }
    } catch { }
    finally { if (first) setLoading(false); }
  }, []);

  useEffect(() => {
    fetchTasks(true);
    const t = setInterval(() => fetchTasks(false), 60000);
    return () => clearInterval(t);
  }, [fetchTasks]);

  const handleClearNew = useCallback(() => {
    setNewCount(0);
    const seen = getSeenIds();
    tasks.forEach(t => seen.add(t.id));
    saveSeenIds(seen);
  }, [tasks]);

  const handleSaved = useCallback(() => {
    setSelectedTask(null);
    fetchTasks(true);
  }, [fetchTasks]);

  // Detail screens take over full-screen
  if (selectedTask) {
    if (selectedTask.type === 'service') {
      return <ServiceDetail task={selectedTask} user={user} onBack={() => setSelectedTask(null)} onSaved={handleSaved}/>;
    }
    if (selectedTask.type === 'sale') {
      return <SaleDetail task={selectedTask} user={user} onBack={() => setSelectedTask(null)} onSaved={handleSaved}/>;
    }
    return <ComplaintDetail task={selectedTask} user={user} onBack={() => setSelectedTask(null)} onSaved={handleSaved}/>;
  }

  const TABS = [
    { key:'tasks',      label:'My Tasks',   Icon: ClipCheck, badge: newCount > 0 ? newCount : null },
    { key:'attendance', label:'Attendance', Icon: Calendar  },
    { key:'salary',     label:'Salary',     Icon: Wallet    },
  ];

  return (
    <div style={{ minHeight:'100vh', background:'#f5f7fa', fontFamily:'Inter,-apple-system,sans-serif' }}>
      {/* Sticky header */}
      <div style={{ background:'var(--navy)', color:'#fff', padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:15 }}>Sakthi Solar</div>
          <div style={{ fontSize:11, opacity:0.7 }}>{user?.name} · Field Staff</div>
        </div>
        <button onClick={() => { logout(); navigate('/login'); }}
          style={{ background:'transparent', border:'1px solid rgba(255,255,255,0.4)', color:'#fff', borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer' }}>
          Logout
        </button>
      </div>

      <div style={{ paddingBottom:72 }}>
        {tab === 'tasks'      && <MyTasks tasks={tasks} loading={loading} onRefresh={() => fetchTasks(true)} onSelectTask={setSelectedTask} onClearNew={handleClearNew}/>}
        {tab === 'attendance' && <MyAttendance/>}
        {tab === 'salary'     && <SalaryAck user={user}/>}
      </div>

      {/* Bottom tab bar */}
      <div style={{ position:'fixed', bottom:0, left:0, right:0, background:'#fff', borderTop:'2px solid #eee', display:'flex', zIndex:100, boxShadow:'0 -2px 12px rgba(0,0,0,0.08)' }}>
        {TABS.map(({ key, label, Icon, badge }) => {
          const active = tab === key;
          return (
            <button key={key} onClick={() => setTab(key)}
              style={{ flex:1, padding:'10px 0 12px', border:'none', background: active ? 'rgba(26,39,68,0.06)' : 'transparent', cursor:'pointer', position:'relative', display:'flex', flexDirection:'column', alignItems:'center', gap:4, WebkitTapHighlightColor:'transparent' }}>
              <Icon s={22} style={{ color: active ? 'var(--navy)' : '#aaa', stroke: active ? 'var(--navy)' : '#aaa' }}/>
              <span style={{ fontSize:11, color: active ? 'var(--navy)' : '#aaa', fontWeight: active ? 700 : 400, lineHeight:1 }}>{label}</span>
              {badge != null && (
                <span style={{ position:'absolute', top:7, left:'50%', transform:'translateX(10px)', background:'#dc2626', color:'#fff', fontSize:10, fontWeight:700, borderRadius:10, padding:'1px 5px', minWidth:16, textAlign:'center', lineHeight:'16px' }}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
